import {
  CampaignStatusesEnum,
  phishingSignsLabels,
  PlaceholderLikelyPick,
  PlaceholderSelectionPreview,
  SimulationSchedulerItem,
} from '@/client/campaigns';
import { SystemRoles } from '@/client/users';
import { TargetEntities, TargetEntity } from '@/common/types';
import { PlaceholderPreviewPanel } from '@/components/campaigns/simulations/PlaceholderPreviewPanel';
import { PlaceholderPreviewTooltip } from '@/components/campaigns/simulations/PlaceholderPreviewTooltip';
import {
  useGeneratePlaceholderLikelyPick,
  usePlaceholderSelectionPreview,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import i18n from '@/i18n';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { EditIcon, getPhishingLanguageCode, TrashIcon } from '@/utils/helpers';
import { t } from 'i18next';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const TOOLTIP_CLOSE_DELAY_MS = 200;
const MIN_DURATION_MS = 60 * 60 * 1000;
const PLACEHOLDER_MIN_DURATION_MS = 24 * 60 * 60 * 1000;
const TIMELINE_INITIAL_CHROME_WIDTH = 296;
const TIMELINE_FALLBACK_WIDTH = 1096;
const PLACEHOLDER_OPACITY = 0.7;

const GanttContainer = styled.div`
  background: white;
  padding: var(--default-padding);
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  border-radius: var(--semi-medium-border-radius);
  border: 1px solid var(--gray-moderate);
`;

const GanttHeader = styled.div<{ $minWidth: number }>`
  display: flex;
  position: relative;
  min-width: ${({ $minWidth }) => $minWidth}px;
`;

const TimeColumnsContainer = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

const monthDayCounts = (start: Date, end: Date): number[] => {
  const counts: number[] = [];

  for (const d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    const monthStart = new Date(d);
    const nextMonthStart = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const from = new Date(Math.max(monthStart.getTime(), start.getTime()));
    const to = new Date(Math.min(nextMonthStart.getTime(), end.getTime()));

    counts.push(
      Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)),
    );
  }

  return counts;
};

const TimelineLabel = styled.div`
  position: sticky;
  left: 0;
  z-index: 3;
  background: white;
  box-shadow: calc(-1 * var(--default-padding)) 0 0 0 white;
  min-width: 220px;
  margin-right: var(--medium-padding);
  font-size: var(--small-font-size);
  font-weight: 600;
  color: var(--black-main);
  display: flex;
  align-items: center;
  padding: var(--small-padding) var(--xsmall-padding);
  border-radius: var(--xxxsmall-border-radius) 0 0 var(--xxxsmall-border-radius);
`;

const TimeColumn = styled.div<{ widthPx: number }>`
  flex: 0 0 ${(props) => props.widthPx}px;
  text-align: left;
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  padding: var(--small-padding) var(--xsmall-padding);
  padding-left: var(--xxxsmall-padding);
  position: relative;
  text-align: left;
  min-width: 0;

  &:last-child {
    border-right: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 0.5px;
    background: var(--gray-moderate);
  }
`;

const GanttRow = styled.div<{ $minWidth: number }>`
  display: flex;
  align-items: center;
  position: relative;
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding: var(--medium-padding) 0;
  gap: var(--medium-padding);

  &:not(.last-row) {
    border-bottom: 1px solid var(--gray-moderate);
  }

  .bar {
    position: absolute;
    width: 0;
    height: 100%;
    transition: width 0.2s ease;
    border-radius: var(--xsmall-border-radius);
    background: var(--green-chart-pale);
    font-size: var(--small-font-size);
    font-weight: 500;

    > * {
      opacity: 0;
      white-space: nowrap;
    }

    svg {
      width: 16px;
      height: 16px;
      margin-left: var(--semi-xsmall-padding);
    }

    svg path {
      fill: var(--green-color);
    }

    i {
      margin-left: var(--semi-xsmall-padding);
      color: var(--green-color);
    }
  }

  .info-container {
    position: sticky;
    left: 0;
    z-index: 3;
    background: white;
    box-shadow: calc(-1 * var(--default-padding)) 0 0 0 white;
    max-width: 220px;
    min-width: 220px;
    min-height: 70px;

    > :not(.bar) {
      transition: opacity 0.2s ease;
    }

    &:hover:not(.disabled) {
      .bar {
        cursor: pointer;
        width: 100%;

        > * {
          opacity: 1;
        }
      }

      > :not(.bar) {
        opacity: 0;
      }
    }
  }
`;

const TaskLabel = styled.div<{ noPaddingTop?: boolean }>`
  width: 100%;
  min-width: 200px;
  margin-right: 16px;
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  padding: var(--xxsmall-padding) 0;
  border-radius: var(--xxxsmall-border-radius);
  line-height: 150%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) => props.noPaddingTop && 'padding-top: 0;'}
`;

const targetNames = (targets?: TargetEntities) =>
  Object.values(targets ?? {})
    .flat()
    .map((target: TargetEntity) => target.name)
    .join(', ');

const SecondaryLabel = styled.div`
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  color: var(--gray-darker);
`;

const TaskBarContainer = styled.div`
  flex: 1;
  height: 26px;
  position: relative;
  border-radius: var(--xxsmall-border-radius);
  margin-right: var(--medium-padding);
  background: var(--gray-soft);
`;

const TaskBar = styled.div<{
  disabled?: boolean;
  left: number;
  width: number;
  color?: string;
  isDragging?: boolean;
  isPlaceholder?: boolean;
}>`
  position: absolute;
  top: 0;
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  height: 26px;
  background: ${(props) => props.color};
  border-radius: var(--xxsmall-border-radius);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: white;
  z-index: ${(props) => (props.isDragging ? 10 : 1)};
  overflow: hidden;

  ${(props) => props.disabled && 'cursor: initial;'}
  ${(props) =>
    props.isPlaceholder &&
    `
    background: var(--gray-darker);
    opacity: ${PLACEHOLDER_OPACITY};
  `}

  .edit-icon {
    opacity: 0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    display: flex;

    svg {
      width: 16px;
      height: 16px;
    }
    svg path {
      fill: var(--red-dark);
    }
  }

  &:hover .edit-icon {
    opacity: 1;
  }

  &[data-dragging='true'] .edit-icon {
    opacity: 1;
  }
`;

const TaskHandle = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${(props) => (props.position === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  width: 6px;
  height: 100%;
  background: var(--red-dark);
  cursor: ${(props) => (props.position === 'left' ? 'w-resize' : 'e-resize')};
  opacity: 0;
  z-index: 5;
  transition: all 0.2s ease-in-out;

  ${TaskBar}:hover & {
    opacity: 1;
  }

  ${TaskBar}[data-dragging="true"] & {
    opacity: 1;
  }
`;

const ActionButton = styled.button<{ $hidden?: boolean }>`
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--xsmall-padding);
  border-radius: var(--xxxsmall-border-radius);
  color: var(--black-main);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover:not([disabled]) {
    background: #f0f0f0;
    color: #333;
  }

  &[disabled] {
    cursor: initial;
    opacity: 0.5;
  }
`;

const CustomTooltip = styled.div<{
  visible: boolean;
  left: number;
}>`
  position: absolute;
  left: ${(props) => props.left}%;
  top: -32px;
  transform: translateX(-50%);
  background: var(--purple-dark);
  color: var(--white-main);
  padding: var(--semi-xsmall-padding) var(--small-padding);
  border-radius: var(--xxxsmall-border-radius);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  pointer-events: none;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  white-space: nowrap;
  text-align: center;
  min-width: 80px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--purple-dark);
  }
`;

const Legend = styled.div`
  position: sticky;
  left: 0;
  display: flex;
  gap: var(--default-padding);
  padding-top: 16px;
  padding-bottom: 16px;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
`;

const LegendDot = styled.div<{ color: string; opacity?: number }>`
  width: 16px;
  height: 16px;
  background: ${(props) => props.color};
  border-radius: 50%;
  opacity: ${(props) => props.opacity ?? 1};
`;

const DateMissingBar = styled.div<{ $readOnly?: boolean }>`
  flex: 1;
  min-height: 40px;
  align-self: stretch;
  position: relative;
  border-radius: var(--xsmall-border-radius);
  margin-right: var(--medium-padding);
  background: var(--yellow-pale);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--xsmall-padding);
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--yellow-dark);

  &:hover {
    opacity: 0.85;
  }

  i {
    font-size: var(--medium-font-size);
  }
`;

const StyledProgressSpinner = styled(ProgressSpinner)`
  width: 16px;
  height: 16px;
`;

export type SimulationSchedulerProps = {
  simulations: SimulationSchedulerItem[];
  deletingSimulationId?: string | null;
  onSimulationChange?: (simulation: SimulationSchedulerItem) => void;
  onSimulationDelete?: (simulationId: string) => void;
  onSimulationEdit?: (simulation: SimulationSchedulerItem) => void;
  onSimulationSendTestEmail?: (simulation: SimulationSchedulerItem) => void;
  showLanguage?: boolean;
  className?: string;
};

export const SimulationScheduler: React.FC<SimulationSchedulerProps> = ({
  simulations,
  deletingSimulationId,
  onSimulationChange,
  onSimulationDelete,
  onSimulationEdit,
  onSimulationSendTestEmail,
  showLanguage = false,
  className,
}) => {
  const isReadOnly = !onSimulationChange && !onSimulationEdit;

  const [draggedSimulation, setDraggedSimulation] = useState<string | null>(
    null,
  );
  const [dragType, setDragType] = useState<
    'move' | 'resize-left' | 'resize-right'
  >('move');
  const [dragOffset, setDragOffset] = useState(0);
  const [tooltipSimulation, setTooltipSimulation] =
    useState<SimulationSchedulerItem | null>(null);
  const [hoveredSimulation, setHoveredSimulation] =
    useState<SimulationSchedulerItem | null>(null);
  const [likelyPicks, setLikelyPicks] = useState<
    Record<string, PlaceholderLikelyPick>
  >({});
  const [generatingPickId, setGeneratingPickId] = useState<string | null>(null);
  const [pickErrorId, setPickErrorId] = useState<string | null>(null);
  const ganttRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [chromeWidth, setChromeWidth] = useState(TIMELINE_INITIAL_CHROME_WIDTH);
  const timelineLabelRef = useRef<HTMLDivElement>(null);
  const timelineTrailingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ganttRef.current;
    if (!element) return;

    const measure = () => {
      const styles = getComputedStyle(element);
      const padding =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      setContainerWidth(element.clientWidth - padding);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const label = timelineLabelRef.current;
    const trailing = timelineTrailingRef.current;
    if (!label || !trailing) return;

    const measure = () => {
      const labelStyles = getComputedStyle(label);
      setChromeWidth(
        label.offsetWidth +
          parseFloat(labelStyles.marginRight) +
          trailing.offsetWidth,
      );
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(label);
    observer.observe(trailing);

    return () => observer.disconnect();
  }, []);
  const taskBarContainerRef = useRef<HTMLDivElement | null>(null);
  const previewAnchorRef = useRef<HTMLDivElement>(null);
  const tooltipCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const inFlightPickIds = useRef<Set<string>>(new Set());

  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const canPreviewPlaceholders =
    currentUser?.role?.code === SystemRoles.DEVELOPER;

  const placeholders = simulations.filter(
    (simulation) => simulation.status === CampaignStatusesEnum.PLACEHOLDER,
  );

  const isPlaceholderPreviewEnabled =
    canPreviewPlaceholders && placeholders.length > 0;

  const selectionInputs = simulations
    .map((simulation) =>
      simulation.status === CampaignStatusesEnum.PLACEHOLDER
        ? `${simulation.id}:${simulation.status}:${
            simulation.startDate?.getTime() ?? ''
          }`
        : `${simulation.id}:${simulation.status}`,
    )
    .sort()
    .join(',');
  const selectionInputsRef = useRef(selectionInputs);

  useEffect(() => {
    selectionInputsRef.current = selectionInputs;
    setLikelyPicks((currentPicks) =>
      Object.keys(currentPicks).length > 0 ? {} : currentPicks,
    );
    setPickErrorId(null);
    setGeneratingPickId(null);
    inFlightPickIds.current.clear();
  }, [selectionInputs]);

  const {
    placeholderSelectionPreview,
    isFetching: isPlaceholderPreviewFetching,
    isError: hasPlaceholderPreviewError,
  } = usePlaceholderSelectionPreview(currentAccount?.id, {
    enabled: isPlaceholderPreviewEnabled,
  });

  const isPlaceholderPreviewCurrent = placeholders.every((placeholder) => {
    const entry = placeholderSelectionPreview?.find(
      (previewEntry) => previewEntry.placeholderId === placeholder.id,
    );

    return (
      !entry ||
      !placeholder.startDate ||
      new Date(entry.launchDate).getTime() === placeholder.startDate.getTime()
    );
  });

  const isPlaceholderPreviewRefreshing =
    isPlaceholderPreviewFetching ||
    (!hasPlaceholderPreviewError && !isPlaceholderPreviewCurrent);

  const { generate: generateLikelyPick } = useGeneratePlaceholderLikelyPick();

  const handleGenerateLikelyPick = async (placeholderId: string) => {
    const accountId = currentAccount?.id;
    if (!accountId) return;
    if (inFlightPickIds.current.has(placeholderId)) return;

    const requestedFor = selectionInputsRef.current;
    const isStillCurrent = () => selectionInputsRef.current === requestedFor;

    inFlightPickIds.current.add(placeholderId);
    setPickErrorId(null);
    setGeneratingPickId(placeholderId);

    try {
      const pick = await generateLikelyPick(accountId, placeholderId);
      if (!isStillCurrent()) return;
      setLikelyPicks((currentPicks) => ({
        ...currentPicks,
        [placeholderId]: pick,
      }));
    } catch {
      if (!isStillCurrent()) return;
      setPickErrorId(placeholderId);
    } finally {
      if (isStillCurrent()) {
        inFlightPickIds.current.delete(placeholderId);
        setGeneratingPickId(null);
      }
    }
  };

  const cancelTooltipClose = useCallback(() => {
    if (!tooltipCloseTimeout.current) return;
    clearTimeout(tooltipCloseTimeout.current);
    tooltipCloseTimeout.current = null;
  }, []);

  const scheduleTooltipClose = useCallback(() => {
    cancelTooltipClose();
    tooltipCloseTimeout.current = setTimeout(
      () => setHoveredSimulation(null),
      TOOLTIP_CLOSE_DELAY_MS,
    );
  }, [cancelTooltipClose]);

  useEffect(() => cancelTooltipClose, [cancelTooltipClose]);

  const handleBarMouseEnter = (simulation: SimulationSchedulerItem) => {
    cancelTooltipClose();
    setHoveredSimulation(simulation);
  };

  const handleBarMouseLeave = (simulation: SimulationSchedulerItem) => {
    if (
      isPlaceholderPreviewEnabled &&
      simulation.status === CampaignStatusesEnum.PLACEHOLDER
    ) {
      scheduleTooltipClose();
      return;
    }

    setHoveredSimulation(null);
  };

  const formatDate = (date: Date) => {
    return moment(date)
      .locale(i18n.language)
      .format(DateFormats.DATEONLY_SHORT_REVERSE);
  };

  const timelineRange = React.useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultEnd = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate(),
    );

    const furthest = simulations.reduce<number>((latest, simulation) => {
      const candidate = simulation.endDate ?? simulation.startDate;
      return candidate && candidate.getTime() > latest
        ? candidate.getTime()
        : latest;
    }, defaultEnd.getTime());

    if (furthest <= defaultEnd.getTime()) {
      return { start, end: defaultEnd };
    }

    const furthestDate = new Date(furthest);
    const end = new Date(
      furthestDate.getFullYear(),
      furthestDate.getMonth() + 1,
      1,
    );

    return { start, end };
  }, [simulations]);

  const pxPerDay = React.useMemo(() => {
    const width = containerWidth || TIMELINE_FALLBACK_WIDTH;
    const columnsWidth = Math.max(1, width - chromeWidth);

    const today = new Date();
    const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultEnd = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate(),
    );
    const defaultDays = monthDayCounts(defaultStart, defaultEnd).reduce(
      (total, days) => total + days,
      0,
    );

    return columnsWidth / Math.max(1, defaultDays);
  }, [containerWidth, chromeWidth]);

  const generateTimeColumns = () => {
    const columns = [];
    const startDate = timelineRange.start;
    const endDate = timelineRange.end;

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setMonth(d.getMonth() + 1)
    ) {
      const monthStart = new Date(d);
      const nextMonthStart = new Date(d.getFullYear(), d.getMonth() + 1, 1);

      const monthStartInTimeline = new Date(
        Math.max(monthStart.getTime(), startDate.getTime()),
      );
      const monthEndInTimeline = new Date(
        Math.min(nextMonthStart.getTime(), endDate.getTime()),
      );

      const daysInMonth = Math.ceil(
        (monthEndInTimeline.getTime() - monthStartInTimeline.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      const widthPx = daysInMonth * pxPerDay;

      const monthName = moment(monthStart)
        .locale(i18n.language)
        .format(DateFormats.MONTH_SHORT);

      columns.push({
        date: new Date(monthStart),
        label: `${monthName}`,
        widthPx,
      });
    }

    return columns;
  };

  const timeColumns = generateTimeColumns();

  const timelineWidth =
    chromeWidth +
    timeColumns.reduce((total, column) => total + column.widthPx, 0);

  const getSimulationPosition = useCallback(
    (simulation: SimulationSchedulerItem) => {
      if (!simulation.startDate || !simulation.endDate)
        return { left: 0, width: 0 };
      const timelineStart = timelineRange.start;
      const timelineEnd = timelineRange.end;
      const totalDays = Math.ceil(
        (timelineEnd.getTime() - timelineStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      // Calculate the visible area of the campaign in the timeline
      const visibleStart = new Date(
        Math.max(simulation.startDate.getTime(), timelineStart.getTime()),
      );
      const visibleEnd = new Date(
        Math.min(simulation.endDate.getTime(), timelineEnd.getTime()),
      );

      if (visibleEnd <= timelineStart) {
        return { left: 0, width: 0 };
      }

      const visibleStartDays = Math.ceil(
        (visibleStart.getTime() - timelineStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      const visibleDurationDays = Math.ceil(
        (visibleEnd.getTime() - visibleStart.getTime()) / (1000 * 60 * 60 * 24),
      );

      const left = Math.max(0, (visibleStartDays / totalDays) * 100);
      const width = Math.max(1, (visibleDurationDays / totalDays) * 100);

      return { left, width };
    },
    [timelineRange],
  );

  const getDateFromPosition = useCallback(
    (position: number) => {
      const today = new Date();
      const startDate = timelineRange.start;
      const endDate = timelineRange.end;

      const totalDays = Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
      );

      const daysFromStart = (position / 100) * totalDays;
      const newDate = new Date(
        startDate.getTime() + daysFromStart * (1000 * 60 * 60 * 24),
      );

      return newDate < today ? new Date(today) : newDate;
    },
    [timelineRange],
  );

  const handleMouseDown = (
    e: React.MouseEvent,
    simulationId: string,
    type: 'move' | 'resize-left' | 'resize-right',
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const taskBarElement =
      type === 'move'
        ? (e.currentTarget as HTMLElement)
        : (e.currentTarget as HTMLElement).parentElement;
    const container = taskBarElement?.parentElement as HTMLDivElement | null;
    if (!container) return;
    taskBarContainerRef.current = container;

    const taskBarRect = container.getBoundingClientRect();
    if (!taskBarRect.width) return;

    const mouseX = e.clientX - taskBarRect.left;
    const simulation = simulations.find((s) => s.id === simulationId);
    if (!simulation) return;

    const { left, width } = getSimulationPosition(simulation);
    const simulationLeftPx = (left / 100) * taskBarRect.width;
    const simulationRightPx = ((left + width) / 100) * taskBarRect.width;

    let offset = 0;
    if (type === 'move') {
      offset = mouseX - simulationLeftPx;
    } else if (type === 'resize-left') {
      offset = mouseX - simulationLeftPx;
    } else if (type === 'resize-right') {
      offset = mouseX - simulationRightPx;
    }

    setDragOffset(offset);
    setDraggedSimulation(simulationId);
    setDragType(type);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggedSimulation || !taskBarContainerRef.current) return;

      const taskBarRect = taskBarContainerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - taskBarRect.left;
      const adjustedX = mouseX - dragOffset;
      const percentage = Math.max(
        0,
        Math.min(100, (adjustedX / taskBarRect.width) * 100),
      );

      const simulation = simulations.find((s) => s.id === draggedSimulation);
      if (!simulation || !simulation.startDate || !simulation.endDate) return;

      let newStartDate = simulation.startDate;
      let newEndDate = simulation.endDate;

      const today = moment().toDate();
      const minDuration =
        simulation.status === CampaignStatusesEnum.PLACEHOLDER
          ? PLACEHOLDER_MIN_DURATION_MS
          : MIN_DURATION_MS;

      if (dragType === 'move') {
        const duration =
          simulation.endDate.getTime() - simulation.startDate.getTime();
        const dateFromPosition = getDateFromPosition(percentage);
        const isToday = moment(dateFromPosition).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 9 ? currentHour + 1 : 9;
        newStartDate = moment(dateFromPosition)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
        newEndDate = moment(newStartDate.getTime() + duration)
          .hour(15)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();

        if (newStartDate < today) {
          newStartDate = new Date(today);
          newEndDate = new Date(today.getTime() + duration);
        }

        const timelineEnd = timelineRange.end;
        if (newEndDate > timelineEnd) {
          newEndDate = new Date(timelineEnd);
          newStartDate = new Date(timelineEnd.getTime() - duration);
        }
      } else if (dragType === 'resize-left') {
        const newStartDateRaw = getDateFromPosition(percentage);

        newStartDate = moment(
          new Date(Math.max(today.getTime(), newStartDateRaw.getTime())),
        ).toDate();

        const maxStartDate = new Date(
          simulation.endDate.getTime() - minDuration,
        );
        if (newStartDate > maxStartDate) {
          newStartDate = new Date(maxStartDate);
        }

        const timelineStart = timelineRange.start;
        if (newStartDate < timelineStart) {
          newStartDate = new Date(timelineStart);
        }
        const isToday = moment(newStartDate).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 9 ? currentHour + 1 : 9;
        newStartDate = moment(newStartDate)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
      } else if (dragType === 'resize-right') {
        const newEndDateRaw = getDateFromPosition(percentage);

        newEndDate = moment(
          new Date(
            Math.max(
              simulation.startDate.getTime() + minDuration,
              newEndDateRaw.getTime(),
            ),
          ),
        ).toDate();

        const timelineEnd = timelineRange.end;
        if (newEndDate > timelineEnd) {
          newEndDate = new Date(timelineEnd);
        }

        if (newEndDate < newStartDate) {
          newEndDate = new Date(newStartDate.getTime() + minDuration);
        }

        const isToday = moment(newEndDate).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 15 ? currentHour + 2 : 15;
        newEndDate = moment(newEndDate)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
      }

      const updatedSimulation = {
        ...simulation,
        startDate: newStartDate,
        endDate: newEndDate,
      };

      onSimulationChange?.(updatedSimulation);
      setTooltipSimulation(updatedSimulation);
    },
    [
      draggedSimulation,
      dragType,
      dragOffset,
      simulations,
      onSimulationChange,
      getDateFromPosition,
      getSimulationPosition,
      timelineRange,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setDraggedSimulation(null);
    setTooltipSimulation(null);
  }, []);

  useEffect(() => {
    if (draggedSimulation) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedSimulation, handleMouseMove, handleMouseUp]);

  const renderDateTooltip = (
    simulation: SimulationSchedulerItem,
    isPlaceholder: boolean,
  ) => {
    const dateRange = `${formatDate(
      simulation.startDate || new Date(),
    )} - ${formatDate(simulation.endDate || new Date())}`;

    return isPlaceholder
      ? `${t('phishing.autoSchedule.placeholder.label')}: ${dateRange}`
      : dateRange;
  };

  const renderPlaceholderPreview = (
    placeholderId: string,
    preview?: PlaceholderSelectionPreview,
  ) => {
    if (isPlaceholderPreviewRefreshing) {
      return <StyledProgressSpinner />;
    }

    return (
      <PlaceholderPreviewPanel
        preview={preview}
        hasLoadError={hasPlaceholderPreviewError}
        likelyPickName={likelyPicks[placeholderId]?.templateName}
        likelyPickExplanation={likelyPicks[placeholderId]?.explanation}
        isGeneratingPick={generatingPickId === placeholderId}
        hasPickError={pickErrorId === placeholderId}
        onGeneratePick={() => handleGenerateLikelyPick(placeholderId)}
      />
    );
  };

  return (
    <GanttContainer className={className} ref={ganttRef}>
      <GanttHeader $minWidth={timelineWidth}>
        <TimelineLabel ref={timelineLabelRef}></TimelineLabel>
        <TimeColumnsContainer>
          {timeColumns.map((column, index) => (
            <TimeColumn key={index} widthPx={column.widthPx}>
              {column.label}
            </TimeColumn>
          ))}
        </TimeColumnsContainer>
        <div ref={timelineTrailingRef} style={{ width: 60 }} />
      </GanttHeader>

      {simulations.map((simulation, index) => {
        const isPlaceholder =
          simulation.status === CampaignStatusesEnum.PLACEHOLDER;
        const isDateInvalid =
          simulation.status === CampaignStatusesEnum.DRAFT &&
          !simulation.startDate;
        const { left, width } = getSimulationPosition(simulation);
        const taskColor = simulation.color;
        const preview =
          isPlaceholderPreviewRefreshing || !isPlaceholderPreviewCurrent
            ? undefined
            : placeholderSelectionPreview?.find(
                (entry) => entry.placeholderId === simulation.id,
              );
        const showPreviewPanel =
          isPlaceholderPreviewEnabled &&
          isPlaceholder &&
          !draggedSimulation &&
          hoveredSimulation?.id === simulation.id &&
          (isPlaceholderPreviewRefreshing ||
            hasPlaceholderPreviewError ||
            !!preview);
        const showDateTooltip =
          !showPreviewPanel &&
          (tooltipSimulation?.id === simulation.id ||
            (hoveredSimulation?.id === simulation.id &&
              !!simulation.startDate &&
              !!simulation.endDate));

        return (
          <GanttRow
            key={simulation.id}
            $minWidth={timelineWidth}
            className={index === simulations.length - 1 ? 'last-row' : ''}
          >
            {isPlaceholder ? (
              <FlexContainer
                direction="column"
                gap={0}
                width="auto"
                align="flex-start"
                className="info-container disabled"
              >
                <AppChip
                  label={
                    preview
                      ? phishingSignsLabels(t)[preview.sign]
                      : t('phishing.autoSchedule.preview.sign.generic')
                  }
                  type={preview ? `phishing-${preview.sign}` : 'green'}
                  size="xs"
                />
                <TaskLabel>
                  {t('phishing.autoSchedule.placeholder.label')}
                </TaskLabel>
                <SecondaryLabel>
                  {t('phishing.autoSchedule.placeholder.description')}
                </SecondaryLabel>
              </FlexContainer>
            ) : (
              <FlexContainer
                direction="column"
                gap={0}
                width="auto"
                align="flex-start"
                className={`info-container ${
                  isReadOnly ||
                  (simulation.disabled &&
                    simulation.status !== CampaignStatusesEnum.SCHEDULED)
                    ? 'disabled'
                    : ''
                }`}
              >
                {simulation.phishingSign && (
                  <AppChip
                    label={t(`campaign.template.${simulation.phishingSign}`)}
                    type="green"
                    size="s"
                  />
                )}
                <TaskLabel noPaddingTop={!simulation.phishingSign}>
                  {simulation.name}
                </TaskLabel>
                <SecondaryLabel>
                  {simulation.targetGroup &&
                  Object.values(simulation.targetGroup)?.length ? (
                    Object.values(simulation.targetGroup)
                      .map((enrolled: TargetEntity[]) =>
                        enrolled.map((enrolled: TargetEntity) => enrolled.name),
                      )
                      .flat()
                      .join(', ')
                  ) : (
                    <span>
                      {t('phishing.simulations.targetGroup.allUsers')}
                    </span>
                  )}{' '}
                  {!!targetNames(simulation.excludes) && (
                    <>
                      {t('generic.excluding')}{' '}
                      {targetNames(simulation.excludes)}{' '}
                    </>
                  )}
                  ({simulation.targetUsersCount})
                </SecondaryLabel>
                {simulation.languageGroupId && simulation.languages?.length
                  ? simulation.languages.map((language) => (
                      <SecondaryLabel
                        key={language}
                        data-testid="simulation-language"
                      >
                        {getPhishingLanguageCode(language)}
                      </SecondaryLabel>
                    ))
                  : showLanguage &&
                    simulation.language && (
                      <SecondaryLabel data-testid="simulation-language">
                        {getPhishingLanguageCode(simulation.language)}
                      </SecondaryLabel>
                    )}
                {!simulation.disabled && !isReadOnly && (
                  <FlexContainer
                    className="bar"
                    justify="center"
                    align="center"
                    onClick={() => onSimulationEdit?.(simulation)}
                  >
                    <span>{t('phishing.simulations.modal.editCampaign')}</span>{' '}
                    <EditIcon />
                  </FlexContainer>
                )}
                {simulation.status === CampaignStatusesEnum.SCHEDULED &&
                  !!onSimulationSendTestEmail && (
                    <FlexContainer
                      className="bar"
                      justify="center"
                      align="center"
                      onClick={() =>
                        onSimulationSendTestEmail(
                          simulation.languageGroupId && simulation.testSendId
                            ? { ...simulation, id: simulation.testSendId }
                            : simulation,
                        )
                      }
                    >
                      <span>{t('simulations.testEmail.send')}</span>{' '}
                      <i className="pi pi-envelope" />
                    </FlexContainer>
                  )}
              </FlexContainer>
            )}
            {isDateInvalid ? (
              <DateMissingBar
                $readOnly={isReadOnly}
                onClick={
                  isReadOnly ? undefined : () => onSimulationEdit?.(simulation)
                }
              >
                <span>{t('phishing.simulations.selectDate')}</span>
                <i className="pi pi-calendar" />
              </DateMissingBar>
            ) : (
              <TaskBarContainer>
                <TaskBar
                  ref={showPreviewPanel ? previewAnchorRef : undefined}
                  left={left}
                  width={width}
                  color={taskColor}
                  isDragging={draggedSimulation === simulation.id}
                  disabled={simulation.disabled || isReadOnly}
                  isPlaceholder={isPlaceholder}
                  data-dragging={draggedSimulation === simulation.id}
                  data-simulation-id={simulation.id}
                  onMouseDown={(e) =>
                    !simulation.disabled && !isReadOnly
                      ? handleMouseDown(e, simulation.id, 'move')
                      : null
                  }
                  onMouseEnter={() => handleBarMouseEnter(simulation)}
                  onMouseLeave={() => handleBarMouseLeave(simulation)}
                >
                  {!simulation.disabled && !isReadOnly && (
                    <>
                      <TaskHandle
                        position="left"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, simulation.id, 'resize-left');
                        }}
                      />
                      {!isPlaceholder && (
                        <div
                          className="edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSimulationEdit?.(simulation);
                          }}
                        >
                          <EditIcon />
                        </div>
                      )}
                      <TaskHandle
                        position="right"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, simulation.id, 'resize-right');
                        }}
                      />
                    </>
                  )}
                </TaskBar>
                {showDateTooltip ? (
                  <CustomTooltip visible={true} left={left + width / 2}>
                    {renderDateTooltip(simulation, isPlaceholder)}
                  </CustomTooltip>
                ) : null}
                {showPreviewPanel ? (
                  <PlaceholderPreviewTooltip
                    anchorRef={previewAnchorRef}
                    onMouseEnter={cancelTooltipClose}
                    onMouseLeave={scheduleTooltipClose}
                  >
                    {renderPlaceholderPreview(simulation.id, preview)}
                  </PlaceholderPreviewTooltip>
                ) : null}
              </TaskBarContainer>
            )}

            <ActionButton
              $hidden={!simulation.canDelete}
              disabled={
                !simulation.canDelete || deletingSimulationId === simulation.id
              }
              onClick={() => onSimulationDelete?.(simulation.id)}
            >
              {deletingSimulationId === simulation.id ? (
                <StyledProgressSpinner />
              ) : (
                <TrashIcon />
              )}
            </ActionButton>
          </GanttRow>
        );
      })}

      <Legend>
        <LegendItem>
          <LegendDot color="var(--green-chart-dark)" />
          <span>{t('phishing.simulations.status.currentlyRunning')}</span>
        </LegendItem>
        <LegendItem>
          <LegendDot color="var(--green-color)" />
          <span>{t('phishing.simulations.status.scheduled')}</span>
        </LegendItem>
        <LegendItem>
          <LegendDot color="var(--red-brown)" />
          <span>{t('phishing.simulations.status.notScheduled')}</span>
        </LegendItem>
        {simulations.some(
          (simulation) =>
            simulation.status === CampaignStatusesEnum.PLACEHOLDER,
        ) && (
          <LegendItem>
            <LegendDot
              color="var(--gray-darker)"
              opacity={PLACEHOLDER_OPACITY}
            />
            <span>{t('phishing.autoSchedule.placeholder.label')}</span>
          </LegendItem>
        )}
        {simulations.some(
          (simulation) =>
            simulation.status === CampaignStatusesEnum.FAILED_LAUNCH,
        ) && (
          <LegendItem>
            <LegendDot color="var(--red-main)" />
            <span>{t('phishing.simulations.status.failedToLaunch')}</span>
          </LegendItem>
        )}
        {simulations.some(
          (simulation) =>
            simulation.status === CampaignStatusesEnum.DRAFT &&
            !simulation.startDate,
        ) && (
          <LegendItem>
            <LegendDot color="var(--yellow-pale)" />
            <span>{t('phishing.simulations.status.dateMissing')}</span>
          </LegendItem>
        )}
      </Legend>
    </GanttContainer>
  );
};
